(function () {
  'use strict';

  const DISCOUNT_RETRY_DELAYS = [0, 350, 900];

  function positiveInteger(value, fallback) {
    const parsed = Number.parseInt(value, 10);
    return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : fallback;
  }

  class BKQuantityBreaks {
    constructor(root) {
      this.root = root;
      this.form = root.closest('form[action*="/cart/add"]') || root.closest('form');
      this.quantity = root.querySelector('[data-bkqb-quantity]');
      this.tiers = Array.from(root.querySelectorAll('[data-bkqb-tier]'));
      this.status = root.querySelector('[data-bkqb-status]');
      this.quantityStatus = root.querySelector('[data-bkqb-quantity-status]');
      this.productId = Number(root.dataset.productId);
      this.cartUrl = root.dataset.cartJsonUrl;
      this.discountTitle = root.dataset.discountTitle;
      this.verifyTimer = null;
      this.verificationSequence = 0;
      this.refreshedDrawerSignature = '';

      if (!this.form || !this.quantity || !this.tiers.length || !this.cartUrl) return;
      this.bind();
      this.syncFromQuantity(false);
    }

    bind() {
      this.tiers.forEach((tier) => {
        tier.addEventListener('change', () => {
          if (!tier.checked) return;
          this.setQuantity(positiveInteger(tier.value, 1), true);
        });
      });

      this.quantity.addEventListener('input', () => this.syncFromQuantity(true));
      this.quantity.addEventListener('change', () => {
        this.quantity.value = positiveInteger(this.quantity.value, 1);
        this.syncFromQuantity(true);
      });

      this.root.querySelector('[data-bkqb-decrease]')?.addEventListener('click', () => {
        this.setQuantity(Math.max(1, positiveInteger(this.quantity.value, 1) - 1), true);
      });
      this.root.querySelector('[data-bkqb-increase]')?.addEventListener('click', () => {
        this.setQuantity(positiveInteger(this.quantity.value, 1) + 1, true);
      });

      this.form.addEventListener('submit', () => this.scheduleVerification(180), { capture: true });
      ['cart-update', 'cart:updated', 'bk:cart-updated', 'cart:refresh'].forEach((eventName) => {
        document.addEventListener(eventName, () => this.scheduleVerification(80));
      });
    }

    setQuantity(nextQuantity, announce) {
      this.quantity.value = Math.max(1, positiveInteger(nextQuantity, 1));
      this.quantity.dispatchEvent(new Event('change', { bubbles: true }));
    }

    syncFromQuantity(announce) {
      const value = Math.max(1, positiveInteger(this.quantity.value, 1));
      const tierValue = value >= 3 ? 3 : value;
      this.tiers.forEach((tier) => {
        const selected = Number(tier.value) === tierValue;
        tier.checked = selected;
        tier.closest('.bkqb__tier')?.classList.toggle('is-selected', selected);
      });
      if (announce) this.announceQuantity();
    }

    announceQuantity() {
      if (!this.quantityStatus) return;
      this.quantityStatus.textContent = `${this.root.dataset.quantityUpdatedCopy} ${positiveInteger(this.quantity.value, 1)}`;
    }

    scheduleVerification(delay) {
      clearTimeout(this.verifyTimer);
      this.verifyTimer = setTimeout(() => this.verifyCartDiscount(), delay);
    }

    showStatus(state) {
      if (!this.status) return;
      const copy = state === 'confirmed' ? this.root.dataset.confirmedCopy : this.root.dataset.fallbackCopy;
      if (this.status.dataset.state === state && this.status.textContent === copy) return;
      this.status.dataset.state = state;
      this.status.textContent = copy;
      this.status.hidden = false;
    }

    hideStatus() {
      if (!this.status) return;
      this.status.hidden = true;
      this.status.removeAttribute('data-state');
      this.status.textContent = '';
    }

    async readCart() {
      const response = await fetch(this.cartUrl, {
        method: 'GET',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) throw new Error(`Cart read failed (${response.status})`);
      return response.json();
    }

    cartHasConfirmedDiscount(cart) {
      return (cart.items || []).some((item) => {
        if (Number(item.product_id) !== this.productId) return false;
        const allocations = item.line_level_discount_allocations || [];
        const discounts = item.discounts || [];
        return allocations.some((allocation) => {
          const application = allocation.discount_application || {};
          return application.title === this.discountTitle && Number(allocation.amount) > 0;
        }) || discounts.some((discount) => discount.title === this.discountTitle && Number(discount.amount) > 0);
      });
    }

    productQuantityInCart(cart) {
      return (cart.items || []).reduce((total, item) => {
        return Number(item.product_id) === this.productId ? total + Number(item.quantity || 0) : total;
      }, 0);
    }

    async refreshConfirmedCartDrawer(cart) {
      const wrapper = document.querySelector('#halo-cart-sidebar .previewCart-wrapper');
      if (!wrapper) return;

      const signature = (cart.items || []).map((item) => {
        return `${item.key}:${item.quantity}:${item.final_line_price}`;
      }).join('|');
      if (!signature || signature === this.refreshedDrawerSignature) return;
      this.refreshedDrawerSignature = signature;

      const drawerUrl = this.cartUrl.replace(/\.js(?:\?.*)?$/, '?view=ajax_side_cart');
      for (const delay of [0, 450, 900]) {
        if (delay) await new Promise((resolve) => setTimeout(resolve, delay));
        try {
          const response = await fetch(drawerUrl, {
            method: 'GET',
            credentials: 'same-origin',
            cache: 'no-store',
            headers: { Accept: 'text/html' }
          });
          if (!response.ok) continue;
          const html = await response.text();
          if (!html.includes('data-bkqb-cart-confirmed')) continue;
          wrapper.innerHTML = html;
          wrapper.querySelectorAll('[data-free-shipping-wrapper]').forEach((element) => {
            element.dispatchEvent(new Event('change', { bubbles: true }));
          });
          document.dispatchEvent(new CustomEvent('bkqb:cart-drawer-refreshed', { detail: { cart } }));
          return;
        } catch (error) {
          // The authoritative Product-page state remains confirmed from cart JSON.
        }
      }
      this.refreshedDrawerSignature = '';
    }

    async verifyCartDiscount() {
      const sequence = ++this.verificationSequence;
      let lastCart = null;
      let cartReadFailed = false;

      for (const delay of DISCOUNT_RETRY_DELAYS) {
        if (delay) await new Promise((resolve) => setTimeout(resolve, delay));
        if (sequence !== this.verificationSequence) return;
        try {
          lastCart = await this.readCart();
          cartReadFailed = false;
          if (this.cartHasConfirmedDiscount(lastCart)) {
            this.showStatus('confirmed');
            await this.refreshConfirmedCartDrawer(lastCart);
            return;
          }
        } catch (error) {
          cartReadFailed = true;
        }
      }

      const expectedQuantity = lastCart ? this.productQuantityInCart(lastCart) : positiveInteger(this.quantity.value, 1);
      if (cartReadFailed || expectedQuantity >= 2) this.showStatus('fallback');
      else this.hideStatus();
    }
  }

  function scan(scope) {
    const searchRoot = scope && scope.querySelectorAll ? scope : document;
    searchRoot.querySelectorAll('[data-bkqb]:not([data-bkqb-ready])').forEach((root) => {
      root.dataset.bkqbReady = 'true';
      new BKQuantityBreaks(root);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan(document));
  else scan(document);
  document.addEventListener('shopify:section:load', (event) => scan(event.target));
})();
